/**
 * Market Intelligence data hooks.
 *
 * Real `useQuery` hooks over the injected source, so every consumer already
 * handles loading and error states correctly. When the source swaps from the
 * seeded mock to `api.get`, nothing here changes.
 *
 * Every request parameter is in the query key: two windows under one key would
 * be one cached answer to two different questions.
 */

import { useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { isoFromDay } from "@/lib/market-intelligence/calendar";
import { MOCK_TODAY_DAY } from "@/lib/market-intelligence/mock/curve-model";
import {
  MI_QUERY_ROOT,
  getMarketIntelligenceSource,
  type OverviewResponse,
  type RecSeries,
  type RegionSummaryResponse,
  type StripHistoryResponse,
} from "@/lib/market-intelligence/source";
import { gasRegionByCode, hubByCode, isoByCode } from "@/lib/market-intelligence/topology";
import { recsFrom, type StripRangeKey } from "@/lib/market-intelligence/view-config";

import type {
  ForwardCurveVintage,
  HourlyIndexRow,
  IndexDailyRow,
  IndexRow,
  IsoCode,
  MarketCapacity,
  MarketCommodity,
  MarketHub,
  MarketRegion,
  MarketTopology,
  StripPeriod,
  StripPoint,
  StripSpec,
} from "@/types/market-intelligence";

/** Assessments land once a day, so the 30s app default refetches for nothing. */
const MARKET_STALE_TIME = 5 * 60_000;

export const marketTopologyKey = () => [MI_QUERY_ROOT, "topology"] as const;

/**
 * `enabled: false` holds the fetch: the top-bar search reads the topology from
 * every module, and asks for it only once someone is searching — and only when
 * they may open Market Intelligence at all.
 */
export function useMarketTopology(options: { enabled?: boolean } = {}) {
  return useQuery<MarketTopology>({
    queryKey: marketTopologyKey(),
    queryFn: () => getMarketIntelligenceSource().getTopology(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: options.enabled ?? true,
  });
}

/**
 * A pricing point's kind (hub, load zone, delivery point), from the topology: what a
 * list row reads to carry its kind's icon (`PointKind.tsx`). `undefined`
 * until the topology lands, or for a code it does not know, so the row shows
 * no icon rather than a wrong one. The topology is fetched once a session, so
 * a lookup per row costs nothing.
 */
export function usePointKind(code: string): MarketHub["kind"] | undefined {
  const { data } = useMarketTopology();
  return data?.hubs.find((h) => h.code === code)?.kind;
}

export const marketOverviewKey = (commodity: MarketCommodity, asOf?: string) =>
  [MI_QUERY_ROOT, "overview", commodity, asOf ?? "latest"] as const;

export function useMarketOverview(commodity: MarketCommodity, asOf?: string) {
  return useQuery<OverviewResponse>({
    queryKey: marketOverviewKey(commodity, asOf),
    queryFn: () => getMarketIntelligenceSource().getOverview({ commodity, asOf }),
    staleTime: MARKET_STALE_TIME,
  });
}

export const marketCapacityKey = (iso: IsoCode | undefined) =>
  [MI_QUERY_ROOT, "capacity", iso ?? "no-iso"] as const;

/**
 * One market's capacity auction results.
 *
 * `null` data is a market that clears no auction — a successful answer, not a
 * failure, so the page renders its empty state rather than an error. No
 * `asOf`: an auction clears once and the result does not get revised, unlike
 * an assessment that lands daily.
 */
export function useMarketCapacity(iso: IsoCode | undefined) {
  return useQuery<MarketCapacity | null>({
    queryKey: marketCapacityKey(iso),
    queryFn: () => getMarketIntelligenceSource().getCapacity({ iso: iso! }),
    enabled: !!iso,
    staleTime: MARKET_STALE_TIME,
  });
}

export const regionSummaryKey = (
  iso: string | undefined,
  commodity: MarketCommodity,
  asOf?: string,
) => [MI_QUERY_ROOT, "region", iso ?? "no-iso", commodity, asOf ?? "latest"] as const;

/** One region's points: an ISO's (`power`) or a gas region's (`gas`). */
export function useRegionSummary(
  iso: string | undefined,
  commodity: MarketCommodity,
  asOf?: string,
) {
  return useQuery<RegionSummaryResponse>({
    queryKey: regionSummaryKey(iso, commodity, asOf),
    queryFn: () =>
      getMarketIntelligenceSource().getRegionSummary({ iso: iso!, commodity, asOf }),
    enabled: !!iso,
    staleTime: MARKET_STALE_TIME,
  });
}

/**
 * One region, either commodity's, by its code: the topology's when it has
 * landed, else the static fallback, so the heading and its state chips do not
 * wait on a fetch the Overview has usually already made.
 *
 * `undefined` for a code neither knows, which a page renders as its empty
 * state.
 */
export function useMarketRegion(
  commodity: MarketCommodity,
  code: string | undefined,
): MarketRegion | undefined {
  const { data } = useMarketTopology();
  if (!code) return undefined;
  if (commodity === "gas") {
    return data?.gasRegions.find((r) => r.code === code) ?? gasRegionByCode(code);
  }
  return data?.isos.find((r) => r.code === code) ?? isoByCode(code);
}

/**
 * What a caller may ask for. The all-tenors form is the Forwards tab's
 * default: it draws five lines and toggles them client-side, so refetching
 * per tenor would be five requests for one screen.
 */
export type StripRequest = StripSpec | { mode: "rolling"; tenors?: readonly number[] };

/** A stable key fragment for a strip selection. */
function specKey(spec: StripRequest): string {
  if (spec.mode === "rolling") {
    if ("tenor" in spec) return `rolling:${spec.tenor}`;
    return `rolling:${(spec.tenors ?? ["all"]).join(",")}`;
  }
  if (spec.mode === "calendar") return `calendar:${spec.year}`;
  return `quarterly:${spec.year}Q${spec.quarter}`;
}

export const stripHistoryKey = (
  hub: string | undefined,
  spec: StripRequest,
  from: string,
  to?: string,
  everyDay = false,
) =>
  [
    MI_QUERY_ROOT,
    "hub",
    hub ?? "no-hub",
    "strips",
    specKey(spec),
    from,
    to ?? "latest",
    everyDay ? "every-day" : "charted",
  ] as const;

export function useStripHistory(
  hub: string | undefined,
  spec: StripRequest,
  from: string,
  to?: string,
) {
  return useQuery<StripHistoryResponse>({
    queryKey: stripHistoryKey(hub, spec, from, to),
    queryFn: () =>
      getMarketIntelligenceSource().getStripHistory({ hub: hub!, spec, from, to }),
    enabled: !!hub,
    staleTime: MARKET_STALE_TIME,
  });
}

/**
 * Several strip specs for ONE hub, fetched in parallel.
 *
 * The Forwards tab's calendar and quarterly modes are multi-select, and
 * `getStripHistory` takes one spec per call — a calendar year is a distinct
 * strip, not a filter over a shared series the way a rolling tenor is. So the
 * tab asks for one query per selected year (at most four, from a fixed list)
 * and each caches independently, which means re-selecting a year it has
 * already shown is instant.
 *
 * Returns points keyed by the caller's own label, so the caller decides how a
 * spec is named rather than re-deriving it from the spec shape.
 */
export function useStripHistoryBySpec(
  hub: string | undefined,
  specs: ReadonlyArray<{ key: string; spec: StripRequest }>,
  from: string,
  to?: string,
  /** Every assessment date, unthinned: for statistics, not charts. */
  everyDay = false,
) {
  const results = useQueries({
    queries: specs.map(({ spec }) => ({
      queryKey: stripHistoryKey(hub, spec, from, to, everyDay),
      queryFn: () =>
        getMarketIntelligenceSource().getStripHistory({ hub: hub!, spec, from, to, everyDay }),
      enabled: !!hub,
      staleTime: MARKET_STALE_TIME,
    })),
  });

  const byKey = new Map<string, StripPoint[]>();
  results.forEach((result, i) => {
    if (result.data) byKey.set(specs[i].key, result.data.points);
  });

  return {
    byKey,
    /** The most recent assessment date any spec reported, for the header. */
    assessedTo: results.find((r) => r.data)?.data?.window.to ?? null,
    isLoading: results.length > 0 && results.some((r) => r.isLoading),
  };
}

/**
 * All five rolling tenors for several hubs at once.
 *
 * The Historical View of Forward Prices grid needs each hub's own strip statistics, and
 * `getStripHistory` is per-hub — so this is the same bounded fan-out as
 * `useIndexSeriesByHub`, for the same reason. See that hook's note.
 */
export function useStripHistoryByHub(
  hubs: readonly string[],
  from: string,
  to?: string,
) {
  const spec: StripRequest = { mode: "rolling" };

  const results = useQueries({
    queries: hubs.map((hub) => ({
      queryKey: stripHistoryKey(hub, spec, from, to),
      queryFn: () =>
        getMarketIntelligenceSource().getStripHistory({ hub, spec, from, to }),
      staleTime: MARKET_STALE_TIME,
    })),
  });

  const byHub = new Map<string, StripPoint[]>();
  results.forEach((result, i) => {
    if (result.data) byHub.set(hubs[i], result.data.points);
  });

  return {
    byHub,
    isLoading: results.length > 0 && results.every((r) => r.isLoading),
    isFetching: results.some((r) => r.isFetching),
  };
}

/**
 * How far back the forward curve's comparison vintage is struck.
 *
 * Exported so the chart and the detail grid label the same date — they read
 * the same two vintages and disagreeing about which would be worse than
 * showing neither.
 */
export const CURVE_VINTAGE_LAG_DAYS = 90;

/** Keyed on the hub alone — see `useForwardCurve` for why not the dates. */
export const forwardCurveKey = (hub: string | undefined) =>
  [MI_QUERY_ROOT, "hub", hub ?? "no-hub", "curve"] as const;

/**
 * One hub's forward curve.
 *
 * A single query shared by the chart and the grid beneath it, so both describe
 * the same assessments. Previously the chart owned the fetch privately, which
 * left nothing for a second consumer to read.
 *
 * **`asOfDates` no longer names real vintages, and the real source ignores it.**
 * It used to ask for today's curve and one from ninety days earlier; the mirror
 * holds exactly two assess dates across the whole table — the newest curve plus
 * one already-settled front month — so that comparison vintage does not exist
 * to fetch. The parameter stays because the seeded generator still honours it,
 * and the key stays keyed on the hub alone: keying it on a date derived from
 * the generator's frozen "today" would cache under a date the server never saw.
 */
export function useForwardCurve(hub: string | undefined, lagDays = CURVE_VINTAGE_LAG_DAYS) {
  const asOfDates = useMemo(
    () => [isoFromDay(MOCK_TODAY_DAY), isoFromDay(MOCK_TODAY_DAY - lagDays)],
    [lagDays],
  );

  return useQuery<ForwardCurveVintage[]>({
    queryKey: forwardCurveKey(hub),
    queryFn: () =>
      getMarketIntelligenceSource().getForwardCurve({ hub: hub!, asOfDates }),
    enabled: !!hub,
    staleTime: MARKET_STALE_TIME,
  });
}

export const indexSeriesKey = (hub: string, months: number) =>
  [MI_QUERY_ROOT, "hub", hub, "index-series", months] as const;

export const hourlyIndexKey = (hub: string | undefined, days: number) =>
  [MI_QUERY_ROOT, "hub", hub ?? "no-hub", "index-hourly", days] as const;

/** The intraday settlement profile for one hub. */
export function useHourlyIndex(hub: string | undefined, days = 7) {
  return useQuery<HourlyIndexRow[]>({
    queryKey: hourlyIndexKey(hub, days),
    queryFn: () =>
      getMarketIntelligenceSource().getHourlyIndex({ hub: hub!, days }),
    enabled: !!hub,
    staleTime: MARKET_STALE_TIME,
  });
}

export const indexDailyKey = (
  hub: string | undefined,
  from: string | null,
  to: string | null,
) => [MI_QUERY_ROOT, "hub", hub ?? "no-hub", "index-daily", from, to] as const;

/** Monthly settled index for one hub — the series behind the Trends view. */
/**
 * One hub's daily settled index between two `yyyy-MM-dd` days — the Trends
 * view's Daily interval. Disabled until both bounds are known.
 */
export function useIndexDaily(
  hub: string | undefined,
  from: string | null,
  to: string | null,
) {
  return useQuery<IndexDailyRow[]>({
    queryKey: indexDailyKey(hub, from, to),
    queryFn: () =>
      getMarketIntelligenceSource().getIndexDaily({ hub: hub!, from: from!, to: to! }),
    enabled: !!hub && !!from && !!to,
    staleTime: MARKET_STALE_TIME,
  });
}

export function useIndexSeries(hub: string | undefined, months = 36) {
  return useQuery<IndexRow[]>({
    queryKey: indexSeriesKey(hub ?? "no-hub", months),
    queryFn: () =>
      getMarketIntelligenceSource().getIndexSeries({ hub: hub!, months }),
    enabled: !!hub,
    staleTime: MARKET_STALE_TIME,
  });
}

/**
 * Index settlement history for several hubs at once.
 *
 * `getIndexSeries` is per-hub, but the Index Prices grid ranks every hub's
 * settlement against its OWN history — so it needs one series per row. A
 * region carries 2–6 hubs, so this is a small, bounded fan-out rather than a
 * page-weight problem, and React Query caches each hub independently: opening
 * a hub's own page afterwards reuses the series already fetched here.
 *
 * `useQueries` rather than a `useQuery` per hub in a loop, because the hub
 * list is data — its length changes between regions, and calling a hook a
 * variable number of times breaks the rules of hooks.
 *
 * Prefer widening the region-summary endpoint when the real API lands; this
 * shape exists because the current source exposes index history one hub at a
 * time.
 */
export function useIndexSeriesByHub(hubs: readonly string[], months = 36) {
  const results = useQueries({
    queries: hubs.map((hub) => ({
      queryKey: indexSeriesKey(hub, months),
      queryFn: () => getMarketIntelligenceSource().getIndexSeries({ hub, months }),
      staleTime: MARKET_STALE_TIME,
    })),
  });

  // A Map keyed by hub, so callers join by code rather than by index — the
  // array order matches `hubs`, but relying on that couples every consumer to
  // the argument's ordering.
  const byHub = new Map<string, IndexRow[]>();
  results.forEach((result, i) => {
    if (result.data) byHub.set(hubs[i], result.data);
  });

  return {
    byHub,
    // Loading only while nothing has arrived yet: the grid renders each row's
    // percentile as it lands rather than blocking the whole table on the
    // slowest hub.
    isLoading: results.length > 0 && results.every((r) => r.isLoading),
    isFetching: results.some((r) => r.isFetching),
  };
}

export const stripPeriodsKey = (hub: string | undefined) =>
  [MI_QUERY_ROOT, "hub", hub ?? "no-hub", "strip-periods"] as const;

/**
 * Which fixed periods a hub actually quotes.
 *
 * The Strips controls used to offer a hardcoded `[2027, 2028, 2029, 2030]`.
 * That is correct for power today, but it is a guess: a calendar year begins
 * quoting about five years out and stops when it rolls off — Cal 26 last
 * quoted 2025-12-30 — and nothing made the chips follow. Asking the server
 * means a chip can never offer a period whose chart would be empty.
 *
 * Returns an empty list while loading and for the seeded generator, which
 * serves no such endpoint; callers fall back to the constant in that case.
 */
export function useStripPeriods(hub: string | undefined) {
  return useQuery<StripPeriod[]>({
    queryKey: stripPeriodsKey(hub),
    queryFn: () => getMarketIntelligenceSource().getStripPeriods({ hub: hub! }),
    enabled: !!hub,
    staleTime: MARKET_STALE_TIME,
  });
}

/**
 * A pricing point by code, resolved from the SERVER's topology, falling back
 * to the static list for the seeded generator — the lookup `HubWorkspacePage`
 * does, for surfaces that hold only a code (a report block). `isLoading` is
 * true only while the topology is in flight AND the code is not yet known.
 */
export function useMarketPoint(code: string | undefined): {
  hub: MarketHub | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useMarketTopology();
  const hub = code ? (data?.hubs.find((h) => h.code === code) ?? hubByCode(code)) : undefined;
  return { hub, isLoading: isLoading && !hub };
}

/**
 * A region's certificate (REC) price series over a history span — the query
 * the region's RECs page runs, under the same key, so a report's RECs block
 * and the page share one cache entry. `max` sends no `from` and reaches the
 * whole mirror (see `recsFrom`).
 */
export function useRecSeries(iso: IsoCode | undefined, range: StripRangeKey) {
  const from = useMemo(() => recsFrom(range, new Date()), [range]);
  return useQuery<RecSeries[]>({
    queryKey: [MI_QUERY_ROOT, "recs", "series", iso ?? "none", range],
    queryFn: () =>
      getMarketIntelligenceSource().getRecSeries({ iso: iso!, ...(from ? { from } : {}) }),
    enabled: !!iso,
    staleTime: 5 * 60_000,
  });
}

/**
 * Several queries combined into one map, keyed by the caller, that keeps its
 * identity until a result actually changes.
 *
 * `useStripHistoryByHub` and `useIndexSeriesByHub` build a fresh map on every
 * render, which is harmless for the grids they feed and wrong for a chart: a
 * new series array each render re-applies the chart's option and drops the
 * reader's zoom. `combine` re-runs only when a result changes, so a chart
 * built from this map re-draws only when there is something new to draw.
 */
function useKeyedQueries<T>(
  entries: ReadonlyArray<{
    key: string;
    queryKey: readonly unknown[];
    queryFn: () => Promise<T>;
  }>,
) {
  const keys = entries.map((e) => e.key).join(" ");
  const combine = useCallback(
    (results: UseQueryResult<T>[]) => {
      const list = keys === "" ? [] : keys.split(" ");
      const byKey = new Map<string, T>();
      const pending = new Set<string>();
      results.forEach((result, i) => {
        if (result.data !== undefined) byKey.set(list[i], result.data);
        else if (result.isLoading) pending.add(list[i]);
      });
      return {
        byKey,
        /** Keys still on their first fetch — not yet "no data". */
        pending,
      };
    },
    [keys],
  );
  return useQueries({
    queries: entries.map(({ queryKey, queryFn }) => ({
      queryKey,
      queryFn,
      staleTime: MARKET_STALE_TIME,
    })),
    combine,
  });
}

/*
 * The other points on a compared chart (`lib/market-intelligence/compare.ts`).
 * Keyed exactly as the single-hub hooks are, so a point already opened on its
 * own page draws here from cache, and the other way round.
 */

/** Strip specs for several hubs, keyed `hub|spec key`. */
export function useStripHistoryForHubs(
  hubs: readonly string[],
  specs: ReadonlyArray<{ key: string; spec: StripRequest }>,
  from: string,
  /** Every assessment date, unthinned: for statistics, not charts. */
  everyDay = false,
) {
  return useKeyedQueries<StripHistoryResponse>(
    from === ""
      ? []
      : hubs.flatMap((hub) =>
          specs.map(({ key, spec }) => ({
            key: `${hub}|${key}`,
            queryKey: stripHistoryKey(hub, spec, from, undefined, everyDay),
            queryFn: () => getMarketIntelligenceSource().getStripHistory({ hub, spec, from, everyDay }),
          })),
        ),
  );
}

/** Forward curves for several hubs, keyed by hub. */
export function useForwardCurvesForHubs(hubs: readonly string[]) {
  const asOfDates = useMemo(
    () => [isoFromDay(MOCK_TODAY_DAY), isoFromDay(MOCK_TODAY_DAY - CURVE_VINTAGE_LAG_DAYS)],
    [],
  );
  return useKeyedQueries<ForwardCurveVintage[]>(
    hubs.map((hub) => ({
      key: hub,
      queryKey: forwardCurveKey(hub),
      queryFn: () => getMarketIntelligenceSource().getForwardCurve({ hub, asOfDates }),
    })),
  );
}

/** Monthly settled index for several hubs, keyed by hub. */
export function useIndexSeriesForHubs(hubs: readonly string[], months: number) {
  return useKeyedQueries<IndexRow[]>(
    hubs.map((hub) => ({
      key: hub,
      queryKey: indexSeriesKey(hub, months),
      queryFn: () => getMarketIntelligenceSource().getIndexSeries({ hub, months }),
    })),
  );
}

/** Daily settled index for several hubs, keyed by hub. Off until both bounds are known. */
export function useIndexDailyForHubs(
  hubs: readonly string[],
  from: string | null,
  to: string | null,
) {
  return useKeyedQueries<IndexDailyRow[]>(
    from && to
      ? hubs.map((hub) => ({
          key: hub,
          queryKey: indexDailyKey(hub, from, to),
          queryFn: () => getMarketIntelligenceSource().getIndexDaily({ hub, from, to }),
        }))
      : [],
  );
}

/** Hourly settlements for several hubs, keyed by hub. */
export function useHourlyIndexForHubs(hubs: readonly string[], days: number) {
  return useKeyedQueries<HourlyIndexRow[]>(
    hubs.map((hub) => ({
      key: hub,
      queryKey: hourlyIndexKey(hub, days),
      queryFn: () => getMarketIntelligenceSource().getHourlyIndex({ hub, days }),
    })),
  );
}
